import { toast } from 'react-toastify';
import { AddToCartBody, Cart, DeleteCartItemParams } from '@core/models/cart.model';
import { IErrorModel, ISuccessResponse } from '@core/models/generic.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import CartService from 'services/api/cart.service';

// -------------------------------------------------------------------------

interface ICartState {
    loading: boolean;
    error: IErrorModel | undefined;
    cart: Cart | null;
}

const initialState: ICartState = {
    loading: false,
    error: {} as IErrorModel,
    cart: null,
};

// -------------------------------------------------------------------------

export const getCartThunk = createAsyncThunk<Cart, void, { rejectValue: IErrorModel }>(
    `/cart/getCart`,
    async (_, { rejectWithValue }: any) => {
        try {
            const response: any = await CartService.getCart();
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch cart', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const addToCartThunk = createAsyncThunk<ISuccessResponse, AddToCartBody, { rejectValue: IErrorModel }>(
    `/cart/addToCart`,
    async (body, { rejectWithValue }: any) => {
        try {
            const response: any = await CartService.addToCart(body);
            toast.success(response.data.message || 'Item added to cart', { autoClose: 3000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to add item to cart', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const deleteCartItemThunk = createAsyncThunk<
    ISuccessResponse,
    DeleteCartItemParams,
    { rejectValue: IErrorModel }
>(`/cart/deleteCartItem`, async (params, { rejectWithValue }: any) => {
    try {
        const response: any = await CartService.deleteCartItem(params);
        toast.success(response.data.message || 'Item removed from cart', { autoClose: 3000 });
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to remove item from cart', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

// -------------------------------------------------------------------------

const cartSlice = createSlice({
    name: `cart`,
    initialState,
    reducers: {
        clearCart(state) {
            state.cart = null;
        },
    },
    extraReducers: (builder) => {
        // Get Cart
        builder.addCase(getCartThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getCartThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.cart = action.payload;
        });
        builder.addCase(getCartThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Add To Cart
        builder.addCase(addToCartThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(addToCartThunk.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(addToCartThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Delete Cart Item
        builder.addCase(deleteCartItemThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(deleteCartItemThunk.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(deleteCartItemThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    },
});

// -------------------------------------------------------------------------

export default cartSlice;
export const { clearCart } = cartSlice.actions;
export const cartReducer = cartSlice.reducer;

